/**
 * Zeichenflaeche ueber einem scrollbaren Bereich.
 *
 * Aufbau
 *   - Die Leinwand liegt als fest positionierte Ebene ueber dem Scrollbereich
 *     und ist genau so gross wie der sichtbare Ausschnitt. Das spart Speicher:
 *     ein langes Lead Sheet braucht keine riesige Leinwand.
 *   - Beim Scrollen wird neu gezeichnet. Die Striche stehen in Inhalts-
 *     koordinaten, also bleiben sie an der richtigen Stelle.
 *   - Die Leinwand faengt nie Eingaben ab (pointer-events: none). Stattdessen
 *     hoert die Flaeche am Scrollbereich mit und reagiert nur auf den Stift.
 *     Dadurch scrollt und tippt der Finger normal weiter.
 *
 * Koordinaten
 *   x = Anteil der Inhaltsbreite, y = Abstand zur Oberkante des Ankers in
 *   Einheiten. Auf Lead Sheets ist der Anker die Textzeile und die Einheit die
 *   Zeilenhoehe – deshalb wandert eine Notiz bei groesserer Schrift mit.
 */
import type { DrawingDoc, Stroke, StrokePoint } from '../../types.ts';
import { MARKER_ALPHA, MARKER_FACTOR, getToolState, notePenUsed, onToolChange } from './tools.ts';

export interface SurfaceOptions {
  /** Der Bereich, der gescrollt wird und ueber dem gezeichnet wird. */
  host: HTMLElement;
  /** Der Inhalt darin – liefert Breite und Ankerpunkte. */
  content: HTMLElement;
  /** Ankerelemente in Reihenfolge. Standard: der Inhalt selbst. */
  getAnchors?: () => HTMLElement[];
  /** Hoehe einer Einheit in Pixeln. Standard: 16. */
  getUnit?: () => number;
  /**
   * Nur auf dem Inhalt zeichnen, nicht auf dem ganzen Bereich.
   * Striche ausserhalb werden abgeschnitten, ein Stift ausserhalb setzt
   * keinen Strich an, und snapshot() liefert genau den Inhalt. Fuer
   * begrenzte Flaechen wie einen Einband.
   */
  clipToContent?: boolean;
  /** Darf der Stift gerade zeichnen? Standard: immer, wenn ein Werkzeug aktiv ist. */
  canDraw?: () => boolean;
  /** Vorhandene Zeichnung. */
  doc: DrawingDoc;
  /** Wird nach Aenderungen aufgerufen (zum Speichern). */
  onChange: (doc: DrawingDoc) => void;
}

export interface DrawingSurface {
  readonly strokeCount: number;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  undo(): void;
  redo(): void;
  clearAll(): void;
  /** Nach Layout-Aenderungen neu messen und zeichnen. */
  refresh(): void;
  /**
   * Ebene zeigen oder verbergen, ohne etwas zu loeschen. Die Ebene liegt
   * ueber der ganzen App; wer ein eigenes Bedienfeld ueber die Flaeche legt,
   * blendet sie solange aus, sonst laegen die Striche darueber.
   */
  setVisible(visible: boolean): void;
  /**
   * Das Gezeichnete als PNG-Data-URL.
   *
   * Nimmt die Leinwand so, wie sie gerade ueber dem Bereich liegt – also
   * genau den sichtbaren Ausschnitt. Damit muss die Strichmathematik nicht
   * ein zweites Mal geschrieben werden, nur um ein Bild zu bekommen.
   * Durchsichtig, wo nichts gezeichnet wurde.
   */
  snapshot(): string;
  destroy(): void;
}

const DEFAULT_UNIT = 16;
const SAVE_DELAY = 500;

/** Die Flaeche, auf der Rueckgaengig und Leeren gerade wirken. */
let activeSurface: DrawingSurface | null = null;
const activeListeners = new Set<() => void>();

export function getActiveSurface(): DrawingSurface | null {
  return activeSurface;
}

/** Meldet sich an, wenn sich die aktive Flaeche oder ihr Inhalt aendert. */
export function onSurfaceChange(listener: () => void): () => void {
  activeListeners.add(listener);
  return () => activeListeners.delete(listener);
}

function notifySurfaceChange(): void {
  for (const listener of activeListeners) listener();
}

/** Zwischenspeicher fuer Werte, die sich nur bei Layout-Aenderungen aendern. */
interface Metrics {
  /** Oberkante jedes Ankers, in Inhaltskoordinaten. */
  tops: number[];
  unit: number;
}

/** Umschliessendes Rechteck eines Strichs in Inhaltskoordinaten. */
interface Bounds {
  top: number;
  bottom: number;
}

export function createDrawingSurface(opts: SurfaceOptions): DrawingSurface {
  const { host, content, onChange } = opts;
  const getAnchors = opts.getAnchors ?? (() => [content]);
  const getUnit = opts.getUnit ?? (() => DEFAULT_UNIT);
  const clipToContent = opts.clipToContent === true;
  const canDraw = opts.canDraw ?? (() => true);

  const doc: DrawingDoc = { ...opts.doc, strokes: opts.doc.strokes.slice() };
  const redoStack: Stroke[] = [];

  const canvas = document.createElement('canvas');
  canvas.className = 'draw-layer';
  document.body.append(canvas);
  const ctx = canvas.getContext('2d');

  let metrics: Metrics = { tops: [0], unit: DEFAULT_UNIT };
  const bounds = new WeakMap<Stroke, Bounds>();
  let drawing: { stroke: Stroke; pixels: { x: number; y: number; p: number }[] } | null = null;
  let saveTimer = 0;
  let dirty = false;
  let frame = 0;
  let destroyed = false;

  /*
   * Zwischenbild waehrend eines Strichs: alle fertigen Striche einmal
   * gerendert. Jedes weitere Bild kopiert nur noch dieses Bild und legt den
   * laufenden Strich darueber, statt hunderte Striche neu zu zeichnen – das
   * haelt die Stiftlatenz auch auf vollen Seiten klein. Gleiches Ergebnis
   * Pixel fuer Pixel; gilt nur, solange sich Lage, Groesse und Striche nicht
   * aendern (layerKey).
   */
  let layer: HTMLCanvasElement | null = null;
  let layerKey = '';
  let generation = 0;

  /* ------------------------------------------------------------- Messen */

  function remeasure(): void {
    const contentRect = content.getBoundingClientRect();
    const anchors = getAnchors();
    const tops = anchors.map((el) => el.getBoundingClientRect().top - contentRect.top);
    metrics = { tops: tops.length > 0 ? tops : [0], unit: getUnit() || DEFAULT_UNIT };
    generation += 1;
    // Zwischengespeicherte Rechtecke passen nicht mehr.
    for (const stroke of doc.strokes) bounds.delete(stroke);
  }

  function topOf(anchor: number): number {
    const index = Math.min(Math.max(anchor, 0), metrics.tops.length - 1);
    return metrics.tops[index] ?? 0;
  }

  /** Grober vertikaler Bereich eines Strichs – fuer das Weglassen ausserhalb. */
  function boundsOf(stroke: Stroke): Bounds {
    const cached = bounds.get(stroke);
    if (cached) return cached;
    const top = topOf(stroke.anchor);
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const point of stroke.points) {
      const y = top + point.y * metrics.unit;
      if (y < min) min = y;
      if (y > max) max = y;
    }
    const pad = stroke.width * metrics.unit * MARKER_FACTOR + 4;
    const value = { top: min - pad, bottom: max + pad };
    bounds.set(stroke, value);
    return value;
  }

  /* ---------------------------------------------------------- Zeichnen */

  function resize(): void {
    const rect = host.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.style.left = `${rect.left}px`;
    canvas.style.top = `${rect.top}px`;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    canvas.width = Math.max(1, Math.round(rect.width * dpr));
    canvas.height = Math.max(1, Math.round(rect.height * dpr));
    ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    generation += 1;
  }

  function paintStroke(
    points: { x: number; y: number; p: number }[],
    stroke: Pick<Stroke, 'tool' | 'color' | 'width' | 'opacity'>,
  ): void {
    if (!ctx || points.length === 0) return;
    const base = stroke.width * metrics.unit;
    // Striche aus frueheren Versionen haben keine Deckkraft gespeichert.
    const alpha = stroke.opacity ?? 1;

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (stroke.tool === 'eraser') {
      // Pixelgenaues Radieren: der Radierer schneidet aus der Ebene aus.
      // Die Striche darunter bleiben als Daten erhalten, sichtbar ist das
      // Ergebnis der Reihenfolge – wie ein echter Radierer auf Papier.
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = base * MARKER_FACTOR;
      strokePath(points);
      ctx.restore();
      return;
    }

    if (stroke.tool === 'marker') {
      // Marker als ein einziger Pfad mit Transparenz – sonst werden die
      // Ueberlappungen an den Segmentgrenzen dunkler.
      ctx.globalAlpha = MARKER_ALPHA * alpha;
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = base * MARKER_FACTOR;
      strokePath(points);
      ctx.restore();
      return;
    }

    // Stift: segmentweise, damit die Druckstaerke die Breite steuert.
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = stroke.color;
    if (points.length === 1) {
      const p = points[0] as { x: number; y: number; p: number };
      ctx.fillStyle = stroke.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, (base * (0.5 + p.p)) / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    for (let i = 1; i < points.length; i += 1) {
      const a = points[i - 1] as { x: number; y: number; p: number };
      const b = points[i] as { x: number; y: number; p: number };
      ctx.lineWidth = base * (0.5 + ((a.p + b.p) / 2) * 1.1);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    ctx.restore();
  }

  function strokePath(points: { x: number; y: number }[]): void {
    if (!ctx) return;
    ctx.beginPath();
    const first = points[0] as { x: number; y: number };
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < points.length; i += 1) {
      const p = points[i] as { x: number; y: number };
      ctx.lineTo(p.x, p.y);
    }
    if (points.length === 1) ctx.lineTo(first.x + 0.01, first.y);
    ctx.stroke();
  }

  /** Rechnet einen gespeicherten Strich in Leinwandkoordinaten um. */
  function toCanvas(stroke: Stroke, contentRect: DOMRect, hostRect: DOMRect) {
    const top = topOf(stroke.anchor);
    return stroke.points.map((point) => ({
      x: contentRect.left + point.x * contentRect.width - hostRect.left,
      y: contentRect.top + top + point.y * metrics.unit - hostRect.top,
      p: point.p,
    }));
  }

  function paint(): void {
    if (!ctx || destroyed) return;
    const hostRect = host.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const key = drawing
      ? [generation, doc.strokes.length, canvas.width, canvas.height, hostRect.left, hostRect.top, contentRect.left, contentRect.top, contentRect.width, contentRect.height].join()
      : '';
    if (drawing && layer && key === layerKey) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    } else {
      paintCommitted(hostRect, contentRect);
      if (drawing) {
        layer ??= document.createElement('canvas');
        layer.width = canvas.width;
        layer.height = canvas.height;
        const lctx = layer.getContext('2d');
        if (lctx) {
          lctx.drawImage(canvas, 0, 0);
          layerKey = key;
        } else {
          layerKey = '';
        }
      }
    }

    // Der Strich, der gerade gezogen wird, liegt schon in Leinwandkoordinaten.
    if (drawing) {
      ctx.save();
      clip(hostRect, contentRect);
      paintStroke(drawing.pixels, drawing.stroke);
      ctx.restore();
    }
  }

  function clip(hostRect: DOMRect, contentRect: DOMRect): void {
    if (!ctx || !clipToContent) return;
    ctx.beginPath();
    ctx.rect(
      contentRect.left - hostRect.left,
      contentRect.top - hostRect.top,
      contentRect.width,
      contentRect.height,
    );
    ctx.clip();
  }

  /** Alle fertigen Striche im sichtbaren Bereich. */
  function paintCommitted(hostRect: DOMRect, contentRect: DOMRect): void {
    if (!ctx) return;
    ctx.save();
    clip(hostRect, contentRect);

    // Sichtbarer Bereich in Inhaltskoordinaten.
    const viewTop = hostRect.top - contentRect.top;
    const viewBottom = viewTop + hostRect.height;

    for (const stroke of doc.strokes) {
      const box = boundsOf(stroke);
      if (box.bottom < viewTop || box.top > viewBottom) continue;
      paintStroke(toCanvas(stroke, contentRect, hostRect), stroke);
    }
    ctx.restore();
  }

  /** Zwischenbild freigeben (Speicher: so gross wie die Leinwand). */
  function dropLayer(): void {
    layerKey = '';
    if (layer) {
      layer.width = 0;
      layer.height = 0;
      layer = null;
    }
  }

  function schedulePaint(): void {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      paint();
    });
  }

  function refresh(): void {
    resize();
    remeasure();
    paint();
  }

  /* -------------------------------------------------------- Speichern */

  function scheduleSave(): void {
    dirty = true;
    notifySurfaceChange();
    clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      dirty = false;
      onChange({ ...doc, strokes: doc.strokes.slice() });
    }, SAVE_DELAY);
  }

  /* ----------------------------------------------------- Stifteingabe */

  /**
   * Stift und Maus zeichnen immer, der Finger nur mit "Finger zeichnet"
   * (ohne Apple Pencil). Ein zweiter Finger bricht den Strich ab: mit zwei
   * Fingern wird gescrollt.
   */
  let fingersDown = 0;
  function isDrawingPointer(ev: PointerEvent): boolean {
    if (ev.pointerType === 'pen') {
      notePenUsed();
      return true;
    }
    if (ev.pointerType === 'mouse') return true;
    return ev.pointerType === 'touch' && getToolState().finger && fingersDown <= 1;
  }

  function pixelFromEvent(ev: PointerEvent, rect: DOMRect): { x: number; y: number; p: number } {
    const pressure = ev.pointerType === 'pen' && ev.pressure > 0 ? ev.pressure : 0.5;
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top, p: pressure };
  }

  /** Liegt der Zeiger auf dem Inhalt? Nur bei begrenzten Flaechen wichtig. */
  function onContent(ev: { clientX: number; clientY: number }): boolean {
    if (!clipToContent) return true;
    const r = content.getBoundingClientRect();
    return ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom;
  }

  function onPointerDown(ev: PointerEvent): void {
    if (!getToolState().active || !canDraw() || !isDrawingPointer(ev)) return;
    if (!onContent(ev)) return;
    // Verhindert Scribble, Fokuswechsel und Textauswahl unter dem Stift.
    ev.preventDefault();
    ev.stopPropagation();
    // Zeiger festhalten, damit ein Strich auch ausserhalb des Bereichs
    // weiterlaeuft. Schlaegt das fehl (z. B. bei erzeugten Ereignissen in
    // Tests), wird trotzdem gezeichnet.
    try {
      host.setPointerCapture(ev.pointerId);
    } catch {
      /* ohne Festhalten weiter */
    }
    remeasure();

    const tool = getToolState();
    drawing = {
      stroke: {
        tool: tool.tool,
        anchor: 0,
        color: tool.color,
        // Staerke in Einheiten speichern, damit sie mit der Schrift mitwaechst.
        width: tool.width / metrics.unit,
        opacity: tool.opacity,
        points: [],
      },
      pixels: [pixelFromEvent(ev, host.getBoundingClientRect())],
    };
    schedulePaint();
  }

  function onPointerMove(ev: PointerEvent): void {
    if (!drawing) return;
    ev.preventDefault();
    // Zwischenpunkte mitnehmen: der Pencil liefert mehr Punkte als Bilder.
    const events = typeof ev.getCoalescedEvents === 'function' ? ev.getCoalescedEvents() : [];
    // Einmal messen je Ereignis, nicht je Zwischenpunkt (erzwingt sonst Layout).
    const rect = host.getBoundingClientRect();
    for (const e of events.length > 0 ? events : [ev]) drawing.pixels.push(pixelFromEvent(e, rect));
    schedulePaint();
  }

  function onPointerUp(ev: PointerEvent): void {
    if (!drawing) return;
    ev.preventDefault();
    try {
      if (host.hasPointerCapture(ev.pointerId)) host.releasePointerCapture(ev.pointerId);
    } catch {
      /* war nicht festgehalten */
    }

    const { stroke, pixels } = drawing;
    drawing = null;
    dropLayer();
    if (pixels.length === 0) return;

    const hostRect = host.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const first = pixels[0] as { x: number; y: number };
    // Anker ist die Zeile, in der der Strich beginnt.
    const startY = hostRect.top + first.y - contentRect.top;
    let anchor = 0;
    for (let i = 0; i < metrics.tops.length; i += 1) {
      if ((metrics.tops[i] as number) <= startY) anchor = i;
      else break;
    }
    const top = topOf(anchor);

    const points: StrokePoint[] = pixels.map((point) => ({
      x: (hostRect.left + point.x - contentRect.left) / (contentRect.width || 1),
      y: (hostRect.top + point.y - contentRect.top - top) / metrics.unit,
      p: point.p,
    }));

    doc.strokes.push({ ...stroke, anchor, points });
    redoStack.length = 0;
    scheduleSave();
    paint();
  }

  /*
   * Scrollen unter dem Stift verhindern.
   *
   * preventDefault auf pointerdown reicht auf iPadOS nicht: das Scrollen
   * läuft dort im Compositor und hört nur auf touch-action oder auf ein
   * abgebrochenes Touch-Ereignis. touch-action können wir nicht abschalten,
   * sonst könnte auch der Finger nicht mehr scrollen.
   *
   * Safari verrät aber pro Berührung, womit sie ausgelöst wurde:
   * touchType ist "stylus" beim Apple Pencil und "direct" beim Finger.
   * Genau dann, und nur dann, brechen wir das Ereignis ab.
   */
  function isStylusTouch(ev: TouchEvent): boolean {
    const lists: TouchList[] = [ev.touches, ev.changedTouches];
    for (const list of lists) {
      for (let i = 0; i < list.length; i += 1) {
        const touch = list.item(i) as (Touch & { touchType?: string }) | null;
        if (touch?.touchType === 'stylus') return true;
      }
    }
    return false;
  }

  function onTouch(ev: TouchEvent): void {
    fingersDown = ev.touches.length;
    if (!getToolState().active || !canDraw()) return;
    // Zwei Finger: scrollen, einen angefangenen Fingerstrich verwerfen.
    if (ev.touches.length >= 2) {
      if (drawing) {
        drawing = null;
        dropLayer();
        paint();
      }
      return;
    }
    const finger = getToolState().finger;
    if (!isStylusTouch(ev) && !finger) return; // Finger scrollt weiter
    if (ev.type === 'touchstart' && !drawing) {
      const touch = ev.changedTouches.item(0);
      if (touch && !onContent(touch)) return;
    }
    ev.preventDefault();
  }

  // passive: false ist Pflicht, sonst darf preventDefault nichts bewirken.
  host.addEventListener('touchstart', onTouch, { passive: false });
  host.addEventListener('touchmove', onTouch, { passive: false });
  host.addEventListener('touchend', (ev) => (fingersDown = ev.touches.length), { passive: true });
  host.addEventListener('touchcancel', (ev) => (fingersDown = ev.touches.length), { passive: true });

  host.addEventListener('pointerdown', onPointerDown, { capture: true });
  host.addEventListener('pointermove', onPointerMove, { capture: true });
  host.addEventListener('pointerup', onPointerUp, { capture: true });
  host.addEventListener('pointercancel', onPointerUp, { capture: true });

  /* ------------------------------------------------------ Beobachter */

  host.addEventListener('scroll', schedulePaint, { passive: true });
  window.addEventListener('scroll', schedulePaint, { passive: true });
  window.addEventListener('resize', refresh);

  const observer = new ResizeObserver(() => refresh());
  observer.observe(host);
  observer.observe(content);

  // Beim Ausschalten des Werkzeugs den halbfertigen Strich verwerfen.
  const stopToolWatch = onToolChange((state) => {
    if (!state.active && drawing) {
      drawing = null;
      dropLayer();
      paint();
    }
  });

  refresh();

  const surface: DrawingSurface = {
    get strokeCount() {
      return doc.strokes.length;
    },
    get canUndo() {
      return doc.strokes.length > 0;
    },
    get canRedo() {
      return redoStack.length > 0;
    },
    undo() {
      const stroke = doc.strokes.pop();
      if (!stroke) return;
      redoStack.push(stroke);
      scheduleSave();
      paint();
    },
    redo() {
      const stroke = redoStack.pop();
      if (!stroke) return;
      doc.strokes.push(stroke);
      scheduleSave();
      paint();
    },
    clearAll() {
      if (doc.strokes.length === 0) return;
      redoStack.push(...doc.strokes.splice(0).reverse());
      scheduleSave();
      paint();
    },
    refresh,
    setVisible(visible: boolean) {
      canvas.style.visibility = visible ? '' : 'hidden';
    },
    snapshot() {
      // Vor dem Auslesen sicherstellen, dass alles Gezeichnete drauf ist.
      paint();
      try {
        if (clipToContent) {
          // Genau den Inhalt ausschneiden: das Bild liegt spaeter
          // deckungsgleich auf diesem Inhalt, nicht auf dem ganzen Bereich.
          const hostRect = host.getBoundingClientRect();
          const r = content.getBoundingClientRect();
          const scale = canvas.width / Math.max(1, hostRect.width);
          const out = document.createElement('canvas');
          out.width = Math.max(1, Math.round(r.width * scale));
          out.height = Math.max(1, Math.round(r.height * scale));
          out
            .getContext('2d')
            ?.drawImage(
              canvas,
              (r.left - hostRect.left) * scale,
              (r.top - hostRect.top) * scale,
              r.width * scale,
              r.height * scale,
              0,
              0,
              out.width,
              out.height,
            );
          return out.toDataURL('image/png');
        }
        return canvas.toDataURL('image/png');
      } catch {
        // Kann bei einer "verunreinigten" Leinwand scheitern; hier zeichnen
        // wir nur selbst, aber sicher ist sicher.
        return '';
      }
    },
    destroy() {
      destroyed = true;
      cancelAnimationFrame(frame);
      clearTimeout(saveTimer);
      dropLayer();
      if (dirty) onChange({ ...doc, strokes: doc.strokes.slice() });
      host.removeEventListener('touchstart', onTouch);
      host.removeEventListener('touchmove', onTouch);
      host.removeEventListener('pointerdown', onPointerDown, { capture: true });
      host.removeEventListener('pointermove', onPointerMove, { capture: true });
      host.removeEventListener('pointerup', onPointerUp, { capture: true });
      host.removeEventListener('pointercancel', onPointerUp, { capture: true });
      host.removeEventListener('scroll', schedulePaint);
      window.removeEventListener('scroll', schedulePaint);
      window.removeEventListener('resize', refresh);
      observer.disconnect();
      stopToolWatch();
      canvas.remove();
      if (activeSurface === surface) activeSurface = null;
      notifySurfaceChange();
    },
  };

  activeSurface = surface;
  notifySurfaceChange();
  return surface;
}
